import StringUtils from "@utils/string.utils";
import { isNumber } from "@utils/var.check";

import type { ComboBoxDropdownOption } from "@plugins/common/types.ts";

export interface DeprecatableSubject {
  deprecated?: boolean;
}

export interface IdentifiableSubject extends DeprecatableSubject {
  id?: number;
  subjectId?: number;
}

export interface NamedSubject extends IdentifiableSubject {
  subjectName: string;
}

export const SUBJECT_FILTER_MODE = {
  ACTIVE_ONLY: "active-only",
  ACTIVE_PLUS_SELECTED: "active-plus-selected",
  ALL_DEPRECATED_LAST: "all-deprecated-last",
} as const;

export type SubjectFilterMode = (typeof SUBJECT_FILTER_MODE)[keyof typeof SUBJECT_FILTER_MODE];

interface OptionGroup {
  header: ComboBoxDropdownOption | null;
  options: ComboBoxDropdownOption[];
}

/**
 * A subject is inactive only when explicitly flagged. An absent flag means active,
 * so the frontend keeps working before the SLS-12117 data patch lands.
 */
const isActive = (subject: DeprecatableSubject): boolean => subject.deprecated !== true;

/**
 * Subject shapes across the codebase identify themselves as either subjectId or id.
 * Values are normalised to string because selector values are strings.
 */
const resolveSubjectId = (subject: IdentifiableSubject): string | undefined => {
  const id = subject.subjectId ?? subject.id;
  return isNumber(id) ? String(id) : undefined;
};

export function filterActiveSubjects<T extends DeprecatableSubject>(subjects: readonly T[]): T[] {
  return subjects.filter((subject) => isActive(subject));
}

export function sortSubjectsDeprecatedLast<T extends DeprecatableSubject & { subjectName: string }>(
  subjects: readonly T[],
): T[] {
  const collator = StringUtils.getStringCollator();

  return [...subjects].sort((a, b) => {
    if (isActive(a) !== isActive(b)) {
      return isActive(a) ? -1 : 1;
    }
    return collator.compare(a.subjectName, b.subjectName);
  });
}

export function buildSubjectOptions(
  subjects: readonly NamedSubject[] | null | undefined,
): ComboBoxDropdownOption[] {
  return (subjects ?? []).reduce<ComboBoxDropdownOption[]>((options, subject) => {
    const value = resolveSubjectId(subject);
    if (value === undefined) {
      return options;
    }

    options.push({
      name: subject.subjectName,
      label: subject.subjectName,
      value,
      header: value === "0",
      deprecated: !isActive(subject),
    });
    return options;
  }, []);
}

/**
 * SLS-12117 prefixes deprecated subject names with a "(DEPRECATED)" marker. Display
 * surfaces that re-case subject names would otherwise corrupt it, so keep the leading
 * marker verbatim and apply the surface's own casing to the remainder only.
 *
 * The match is case-insensitive because SLS-12117 and the story's visual references
 * disagree on the marker's casing, but it is anchored to the marker word rather than to
 * any leading bracket, so a subject legitimately named "(EXPRESS) ENGLISH" re-cases
 * normally instead of being mistaken for a deprecation marker.
 */
const DEPRECATED_MARKER = /^(\s*\(deprecated\)\s*)([\s\S]*)$/i;

export function formatSubjectDisplayName(
  subjectName: string | null | undefined,
  transform: (value: string) => string,
): string {
  if (!subjectName) {
    return "";
  }

  const marked = subjectName.match(DEPRECATED_MARKER);
  return marked ? `${marked[1]}${transform(marked[2])}` : transform(subjectName);
}

/**
 * Composes a "<level> <subject>" card title with the deprecation marker hoisted to the
 * front, so it reads "(DEPRECATED) Primary 5 Art" rather than leaving the marker stranded
 * mid-string as "Primary 5 (DEPRECATED) Art". The marker keeps its original casing while
 * the composed remainder takes the surface's own casing.
 */
export function formatSubjectTitleWithLevel(
  levelName: string | null | undefined,
  subjectName: string | null | undefined,
  transform: (value: string) => string,
): string {
  const marked = (subjectName ?? "").match(DEPRECATED_MARKER);
  const marker = marked ? marked[1].trim() : "";
  const bareSubject = marked ? marked[2] : (subjectName ?? "");

  const composed = [levelName ?? "", bareSubject]
    .map((part) => part.trim())
    .filter((part) => part !== "")
    .join(" ");
  const label = composed === "" ? "" : transform(composed);

  return [marker, label].filter((part) => part !== "").join(" ");
}

export function formatSubjectTitleCase(subjectName: string | null | undefined): string {
  return formatSubjectDisplayName(subjectName, StringUtils.toTitleCaseWithSpecialCharacters);
}

export function formatSubjectWordsTitleCase(subjectName: string | null | undefined): string {
  return formatSubjectDisplayName(subjectName, StringUtils.toTitleCase);
}

/**
 * Applies a selection policy to a built option list, preserving the header groups the
 * list may already carry. Deprecated options sink to the end of their own group rather
 * than the end of the list, and a header whose options are all removed is dropped with
 * them. Relative order is otherwise preserved, so a caller that supplied its own
 * ordering keeps it; use sortSubjectsDeprecatedLast at source for alphabetical order.
 */
export function applySubjectFilterMode(
  options: readonly ComboBoxDropdownOption[],
  mode: SubjectFilterMode,
  selectedValues: readonly string[],
): ComboBoxDropdownOption[] {
  const selected = new Set(selectedValues);

  const keep = (option: ComboBoxDropdownOption): boolean => {
    if (isActive(option)) {
      return true;
    }
    if (mode === SUBJECT_FILTER_MODE.ALL_DEPRECATED_LAST) {
      return true;
    }
    return mode === SUBJECT_FILTER_MODE.ACTIVE_PLUS_SELECTED && selected.has(option.value);
  };

  const groups: OptionGroup[] = [{ header: null, options: [] }];
  options.forEach((option) => {
    if (option.header) {
      groups.push({ header: option, options: [] });
    } else {
      groups[groups.length - 1].options.push(option);
    }
  });

  return groups.flatMap((group) => {
    const kept = group.options.filter((option) => keep(option));
    if (kept.length === 0) {
      return [];
    }

    const ordered = [
      ...kept.filter((option) => isActive(option)),
      ...kept.filter((option) => !isActive(option)),
    ];
    return group.header ? [group.header, ...ordered] : ordered;
  });
}
